import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { MedicaoContratoItem } from "../../../../domain/models/medicaoContrato";
import { useCallback } from "react";

type Props = {
  open: boolean;
  medicoes: MedicaoContratoItem[] | [];
  onClose: () => void;
};

export const MedicoesContratoModal = ({ open, medicoes, onClose }: Props) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Formatador de Moeda (BRL)
  const formatarMoeda = (valor?: number | null) => {
    return (valor ?? 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  // Formatador de Data (dd/MM/yyyy)
  const formatarData = (data?: string | Date | null) => {
    if (!data) return "-";
    const d = new Date(data);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("pt-BR");
  };

  // Renderizador condicional do Status / Pagamento
  const renderStatusBadge = (med: MedicaoContratoItem) => {
    if (med.PgtoProvisionado) {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
          Provisionado
        </span>
      );
    }

    if (med.Status === 2) {
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
          Aprovada
        </span>
      );
    }

    return (
      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
        Pendente
      </span>
    );
  };

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      onClose={handleClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "46rem",
          },
        },
      }}
    >
      <DialogTitle className="flex justify-between items-center text-gray-800 dark:text-gray-100">
        <span>Medições do Contrato</span>
      </DialogTitle>

      <DialogContent dividers>
        <div className="w-full min-h-[160px] flex flex-col p-1 overflow-x-auto">
          {medicoes && medicoes.length > 0 ? (
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                  <th className="py-2.5 px-3">Nº Medição</th>
                  <th className="py-2.5 px-3">Data</th>
                  <th className="py-2.5 px-3">Situação</th>
                  <th className="py-2.5 px-3 text-right">Retenção</th>
                  <th className="py-2.5 px-3 text-right">Valor Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {medicoes.map((med, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="py-3 px-3 font-medium text-gray-800 dark:text-gray-200">
                      {med.NrMedicao ? `#${med.NrMedicao}` : "-"}
                    </td>
                    <td className="py-3 px-3 text-gray-600 dark:text-gray-300">
                      {formatarData(med.DataMedicao)}
                    </td>
                    <td className="py-3 px-3">
                      {renderStatusBadge(med)}
                    </td>
                    <td className="py-3 px-3 text-right text-gray-600 dark:text-gray-300">
                      {formatarMoeda(med.ValorRetencao)}
                    </td>
                    <td className="py-3 px-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                      {formatarMoeda(med.ValorTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="w-full h-full min-h-[140px] flex items-center justify-center text-gray-500 text-sm font-medium">
              Nenhuma medição aprovada ou provisionada para este contrato.
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <div className="w-full items-center justify-end flex p-2 gap-4">
          <button
            type="button"
            onClick={handleClose}
            className="p-2 px-4 hover:bg-hover-light-gray dark:hover:bg-border-dark-gray rounded-[4px] font-semibold text-sm text-black dark:text-white transition-colors"
          >
            Fechar
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};